import registry from "~/components/DoRegistry.js";

/**
 * Used by HandsOnTable to get the drop-down values for the selected cell
 * @param hotInstance
 * @param digitalSpotLengthOptions
 * @returns {Function} called by the HandsOnTable
 */
function getOptionsHandler(hotInstance, digitalSpotLengthOptions) {
    return function(value, process) {
        var spotLengths = null;
        var selectedCell = hotInstance.getSelected();
        if (isArrayWithData(selectedCell)) {
            var row = selectedCell[0][0];
            spotLengths = getDigitalSpotInfo(digitalSpotLengthOptions, hotInstance.getSourceDataAtRow(row)).spotLengthOptions;
        }
        if (Array.isArray(spotLengths)) {
            process(spotLengths);
        } else {
            process([]);
        }
    }
}


/**
 * Get the Digital Spot options for a given asset.
 *
 * An asset is digital if there is a digitalSpotLengthOptions record for it, OR if the Format contains the word "Digital".
 * However, if a format is selected that DOES NOT contain "Digital", that will override the asset to be non-digital (even if there are digitalSpotLengthOptions for it).
 *
 * @param digitalSpotLengthOptions built by AssetServiceImpl.getDigitalSpotLengthOptions
 * @param rowData the HandsOnTable row
 * @returns an object
 */
function getDigitalSpotInfo (digitalSpotLengthOptions, rowData) {
    if (typeof rowData != 'object') {
        return {};
    }
    var inventoryNumber = !rowData.inventoryNumber ? "" : rowData.inventoryNumber.toUpperCase();
    var geopathId = !rowData.tabPanelId ? "" : rowData.tabPanelId;
    var commbId = !rowData.commbId ? "" : rowData.commbId;
    var sellingCompanyName = !rowData.vendor ? "" : rowData.vendor;
    var index = inventoryNumber + "-" + geopathId + "-" + commbId + "-" + sellingCompanyName;
    var options = digitalSpotLengthOptions[index] || {notDigital: true} ;
    if (rowData.format !== null && /\S/.test(rowData.format)) {
        options.notDigital = !/Digital/i.test(rowData.format);
    }
    // console.log("index="+index+", options="+JSON.stringify(options));
    return options;
}

/**
 * Validates a spotLength value in a given row, returning a boolean
 * @param hotInstance
 * @param digitalSpotLengthOptions
 * @param row
 * @param newValue
 * @returns {boolean} true if valid
 */
function validate(hotInstance, digitalSpotLengthOptions, isValid, newValue, row, columnName) {
    if (columnName == "spotLength") {
        isValid = "" == analyzeSpotLength(hotInstance, digitalSpotLengthOptions, row, newValue);
    }
    if (columnName == "digitalSpots") {
        isValid = checkDigitalSpots(hotInstance, digitalSpotLengthOptions, row, newValue);
    }
    return isValid;
}

//For non-digital assets: digitalSpots should be empty or zero. For digital assets: digitalSpots should be a positive integer, or can also be 0 if SpotLength = "N/A".
function checkDigitalSpots(hotInstance, digitalSpotLengthOptions, row, newValue) {
    var isValid = false;
    var digitalSpotInfo = getDigitalSpotInfo(digitalSpotLengthOptions, hotInstance.getSourceDataAtRow(row));
    if (digitalSpotInfo.notDigital) {
        isValid = (newValue == "" || newValue == 0 || newValue == null);
    } else {
        var newValueNum = Number(newValue);
        if (newValue !== "" && newValue !== null && Number.isInteger(newValueNum)) {
            if (newValueNum > 0) {
                isValid = true;
            } else if (newValueNum == 0 && hotInstance.getDataAtCell(row, 'spotLength') == "N/A") {
                isValid = true;
            }
        }
    }
    return isValid;
}

/**
 * Analyzes/Validates a spotLength value, returning a string describing any problem.
 * 1) If the asset has Spot Lengths, must choose one of them.
 * 2) If there are no Spot Lengths, must be Blank or >= 1, but <= the asset's LoopLength (provided LoopLength is > 1)
 * 3) "N/A" is valid only if digitalSpots is 0
 * @param hotInstance
 * @param digitalSpotLengthOptions
 * @param row
 * @param newValue
 * @returns {string}
 */
function analyzeSpotLength(hotInstance, digitalSpotLengthOptions, row, newValue) {
    var digitalSpotInfo = getDigitalSpotInfo(digitalSpotLengthOptions, hotInstance.getSourceDataAtRow(row));
    if (newValue == "N/A") {
        if (hotInstance.getDataAtCell(row, 'digitalSpots') == "0" || digitalSpotInfo.notDigital) {
            return "";
        }
    }
    var spotLengths = digitalSpotInfo.spotLengthOptions;
    var spotLengthsAreDefined = isArrayWithData(spotLengths);
    var newValueNum = Number(newValue);
    let col = hotInstance.propToCol("spotLength");
    if (digitalSpotInfo.notDigital) {
        //set cell renderer to get rid of the drop-down arrow
        hotInstance.setCellMeta(row, col, "renderer", "text");
        if (newValue !== "" && newValue !== null) {
            return "must be blank or N/A when asset is non-digital";
        }
    }
    else if (spotLengthsAreDefined) {
        //set cell renderer to get display the drop-down arrow
        hotInstance.setCellMeta(row, col, "renderer", "autocomplete");
        if (!_.contains(spotLengths, newValueNum)) {
            return "must choose value from dropdown";
        }
    } else {
        //set cell renderer to get rid of the drop-down arrow
        hotInstance.setCellMeta(row, col, "renderer", "text");
        if (newValue !== "" && newValue !== null) {
            var loopLength = digitalSpotInfo.loopLength;
            if (loopLength > 1) {
                if (_.isNaN(newValueNum) || newValueNum < 1 || newValueNum > loopLength) {
                    return "must be blank or between 1 and " + loopLength;
                }
            } else {
                if (_.isNaN(newValueNum) || newValueNum < 1) {
                    return "must be blank or >= 1";
                }
            }
        }
    }
    return "";
}


/**
 * Watches for changes.
 * - Revalidate the row when Format, digitalSpots, or spotLength changes (because these are interrelated)
 *
 * @param hotInstance
 * @param digitalSpotLengthOptions
 * @param changes 2-dimension array [[row, prop, oldVal, newVal], ...]
 */
function beforeChange(hotInstance, digitalSpotLengthOptions, changes, source) {
    if (isArrayWithData(changes)) {
        for (var i = 0, len = changes.length; i < len; ++i) {
            let column = changes[i][1];
            let row = changes[i][0];
            if (column == "format" || column == "digitalSpots" || column == "spotLength") {
                setTimeout(function () {
                    hotInstance.validateRows([row], function () {
                        hotInstance.render();
                    })
                }, 1);
            }
        }
    }
}


/**
 * Initializes Digital Spots selectors and validations in HandsOnTable
 *
 * When using this:
 * 1) Make sure the Controller has added "digitalSpotLengthOptions" to the ModelMap by calling assetService.addDigitalSpotsInfoToModelMap()
 * 2) If in domedia, be sure to include spotLengths.js (this is done automatically in D via staticResources.jspx)
 * 3) Then call this function via the HandsOnTable afterInit hook like this:
 * &nbsp;&nbsp;  afterInit: function(){domedia.src.util.hotTable.spotLengths.initDigitalSpots(this, ${digitalSpotLengthOptions});}
 *
 * @param hotInstance
 * @param digitalSpotLengthOptions
 */
function initDigitalSpots(hotInstance, digitalSpotLengthOptions, performValidation) {
    window.digitalSpotLengthOptions = digitalSpotLengthOptions != null ? digitalSpotLengthOptions : {};
    window.getDigitalSpotLenOptions = getOptionsHandler(hotInstance, digitalSpotLengthOptions);
    hotInstance.addHook('beforeChange', function(changes, source) {beforeChange(hotInstance, digitalSpotLengthOptions, changes, source);});
    hotInstance.addHook('afterValidate', function(isValid, value, row, columnName, source) {return validate(hotInstance, digitalSpotLengthOptions, isValid, value, row, columnName);});
    if (performValidation) {
        setTimeout(function () {
            validateNonEmptyRows(hotInstance, function () {
                hotInstance.render();
            })
        }, 1);
    }
}

function isArrayWithData(arr) {
    return (Array.isArray(arr) && arr.length > 0);
}


registry.register(__filename, {getOptionsHandler, validate, beforeChange, initDigitalSpots});
export {
    getOptionsHandler, validate, beforeChange, initDigitalSpots
}
